import { Card, Flex, Heading, HStack, Stack, Text } from '@chakra-ui/react';
import { useMemo } from 'react';
import { BaseButton } from '@cks-internal/react-base';
import { faCreditCard } from '@fortawesome/free-solid-svg-icons';
import { useShoppingCartData } from '../shoppingCart/hooks/useShoppingCartData.tsx';
import { CartSidebarItem } from './CartSidebarItem.tsx';
import { formatPrice } from '../../../components/common/PriceTag.tsx';

type CartSidebarProps = {
  onViewCart: () => void;
  onCheckout: () => void;
};

const EmptyCartState = () => (
  <Flex direction='column' align='center' justify='center' py='4' px='3' color='gray.400'>
    <Text fontWeight='medium' fontSize='sm' color='gray.600'>
      Your cart is empty
    </Text>
    <Text fontSize='xs' color='gray.500'>
      Add products to get started
    </Text>
  </Flex>
);

export const CartSidebar = (props: CartSidebarProps) => {
  const { onCheckout } = props;
  const { combinedItems, removeItem } = useShoppingCartData();

  const subtotal = useMemo(() => {
    return combinedItems.reduce((total, item) => {
      if (item.Price) {
        // Price is already the full per-line total (quantity included), so do not multiply again.
        return total + item.Price;
      }
      return total;
    }, 0);
  }, [combinedItems]);

  const itemCount = combinedItems.length;
  const isEmpty = itemCount === 0;

  return (
    <Card.Root variant='outline' height='fit-content' bg='white'>
      <Card.Header py='3' px='4' borderBottomWidth='1px'>
        <HStack justify='space-between'>
          <Heading size='sm'>Your Cart</Heading>
          {!isEmpty && (
            <Text fontSize='sm' color='gray.500'>
              {itemCount} {itemCount === 1 ? 'item' : 'items'}
            </Text>
          )}
        </HStack>
      </Card.Header>

      <Card.Body p='0' overflowY='auto' maxH='400px'>
        {isEmpty ? (
          <EmptyCartState />
        ) : (
          <Stack gap='0' px='4' py='2'>
            {combinedItems.map((item) => (
              <CartSidebarItem key={item.StorageId} model={item} onRemove={() => removeItem(item.StorageId)} />
            ))}
          </Stack>
        )}
      </Card.Body>

      {!isEmpty && (
        <Card.Footer flexDir='column' gap='3' borderTopWidth='1px' py='3' px='4'>
          <HStack justify='space-between' width='full'>
            <Text fontWeight='medium'>Subtotal</Text>
            <Text fontWeight='bold' fontSize='lg'>
              {formatPrice(subtotal)}
            </Text>
          </HStack>
          <Stack width='full' gap='2'>
            <BaseButton icon={faCreditCard} onClick={onCheckout} width='full'>
              Checkout
            </BaseButton>
          </Stack>
        </Card.Footer>
      )}
    </Card.Root>
  );
};
